import { useState, useEffect } from 'react';
import { Message } from '../components/ChatPanel/ChatPanel';
// import { useFilters } from '../context/FilterContext';
import Cookies from 'js-cookie';
import {jwtDecode} from 'jwt-decode';

interface ChatOperations {
  messages: Message[];
  messageDates: string[];
  input: string;
  setInput: (input: string) => void;
  isSending: boolean;
  currentInquiryId: number | null;
  isConversationSelected: boolean;
  handleSendMessage: (message?: string, metadata?: MessageMetadata) => Promise<void>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setMessageDates: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentInquiryId: React.Dispatch<React.SetStateAction<number | null>>;
  setIsConversationSelected: React.Dispatch<React.SetStateAction<boolean>>;
  clearChatState: () => void;
}

interface MessageMetadata {
  action?: string;
  metric?: string;
  section?: string;
  filter_info?: { [key: string]: any };
  selected_time_period?: string;
  start_date?: string;
  end_date?: string;
  is_quick_reply?: boolean;
}

interface DecodedToken {
  user_id: number;
  org_id: number;
}

const BASE_DB_URL = import.meta.env.VITE_BASE_DATABASE_URL;
const BASE_ORCANA_URL = import.meta.env.VITE_BASE_SERVER_URL;

export const useChatOperations = (
  onInquiryUpdate?: () => void,
  inquiryTitle: string = 'New Analysis'
): ChatOperations => {
  const token = Cookies.get('accessToken');
  let user_id = 133;
  let org_id = 21;

  try {
    if (!token) throw new Error('No token');
    const decoded = jwtDecode<DecodedToken>(token);
    user_id = decoded.user_id;
    org_id = decoded.org_id;
  } catch {
    console.warn('JWT decode failed, using defaults');
  }

  const [messages, setMessages] = useState<Message[]>(() =>
    JSON.parse(localStorage.getItem('chatMessages') || '[]')
  );
  const [messageDates, setMessageDates] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem('chatMessageDates') || '[]')
  );
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [currentInquiryId, setCurrentInquiryId] = useState<number | null>(() =>
    JSON.parse(localStorage.getItem('currentInquiryId') || 'null')
  );
  const [isConversationSelected, setIsConversationSelected] = useState<boolean>(() =>
    (JSON.parse(localStorage.getItem('chatMessages') || '[]') as Message[]).length > 0
  );

  // Flag to prevent repeated summarization
  const [hasSummarized, setHasSummarized] = useState(false);

  useEffect(() => {
    localStorage.setItem('chatMessages', JSON.stringify(messages));
  }, [messages]);
  useEffect(() => {
    localStorage.setItem('chatMessageDates', JSON.stringify(messageDates));
  }, [messageDates]);
  useEffect(() => {
    localStorage.setItem('currentInquiryId', JSON.stringify(currentInquiryId));
  }, [currentInquiryId]);
  useEffect(() => {
    localStorage.setItem('isConversationSelected', JSON.stringify(isConversationSelected));
  }, [isConversationSelected]);

  const clearChatState = () => {
    localStorage.removeItem('chatMessages');
    localStorage.removeItem('chatMessageDates');
    localStorage.removeItem('currentInquiryId');
    setMessages([]);
    setMessageDates([]);
    setCurrentInquiryId(null);
    setInput('');
    setIsConversationSelected(false);
    setHasSummarized(false);
  };

  const handleSendMessage = async (message?: string, metadata?: MessageMetadata) => {
    const messageToSend = message || input;
    if (!messageToSend.trim()) return;
    setIsSending(true);

    try {
      let localInquiryId = currentInquiryId;

      if (!localInquiryId || messages.length === 0) {
        const res = await fetch(`${BASE_DB_URL}/create-inquiry/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id, org_id }),
        });
        const { status, inquiry_id } = await res.json();
        if (status === 'success') {
          localInquiryId = inquiry_id;
          setCurrentInquiryId(inquiry_id);
          setIsConversationSelected(true);
          onInquiryUpdate?.();
        }
      }
      if (!localInquiryId) throw new Error('Missing inquiry ID');

      // Save user message
      const userRes = await fetch(`${BASE_DB_URL}/create_message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inquiry_id: localInquiryId, user_id, message_source: 'user', body_original: messageToSend }),
      });
      const { message_id } = await userRes.json();
      const userMessage: Message = {
        message_id,
        inquiry_id: localInquiryId,
        user_id,
        created_at: new Date().toISOString(),
        message_source: 'user',
        body_original: messageToSend,
        body_rephrased: null,
        intent: '',
        analytics_run_id: null,
      };
      setMessages((m) => [...m, userMessage]);
      setMessageDates((d) => [
        ...d,
        new Date().toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      ]);
      setInput('');

      // Bot response
      const botRes = await fetch(`${BASE_ORCANA_URL}/generate-response-agentic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id, org_id, inquiry_id: localInquiryId, latest_message: messageToSend, inquiry_history: [...messages, userMessage], metadata: metadata || null }),
      });
      const botData = await botRes.json();
      if (botData.messageBody) {
        const mm: any = {};
        ['image', 'visualMetadata', 'hoverInfo', 'quickReplies', 'code', 'code_plan', 'user_plan'].forEach((k) => {
          if (botData[k]) mm[k] = botData[k];
        });
        const botMsgRes = await fetch(`${BASE_DB_URL}/create_message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ inquiry_id: localInquiryId, user_id, message_source: 'orcana', body_original: botData.messageBody, message_metadata: Object.keys(mm).length ? mm : undefined }),
        });
        const { message_id: bot_id } = await botMsgRes.json();
        const botMessage: Message = {
          message_id: bot_id,
          inquiry_id: localInquiryId,
          user_id,
          created_at: new Date().toISOString(),
          message_source: 'orcana',
          body_original: botData.messageBody,
          body_rephrased: null,
          intent: '',
          analytics_run_id: null,
          image: botData.image,
          message_metadata: mm,
        };
        setMessages((m) => [...m, botMessage]);
        setMessageDates((d) => [
          ...d,
          new Date().toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        ]);
      }

      // Update last_message_time
      await fetch(`${BASE_DB_URL}/update-inquiry/${localInquiryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ last_message_time: new Date().toISOString() }),
      });
      onInquiryUpdate?.();
    } catch (err) {
      console.error('handleSendMessage error:', err);
      const errMsg: Message = {
        message_id: Date.now(),
        inquiry_id: currentInquiryId || 0,
        user_id,
        created_at: new Date().toISOString(),
        message_source: 'orcana',
        body_original: 'Sorry, something went wrong while answering.',
        body_rephrased: null,
        intent: '',
        analytics_run_id: null,
      };
      setMessages((m) => [...m, errMsg]);
      setMessageDates((d) => [
        ...d,
        new Date().toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  // Summarization effect (only once)
  useEffect(() => {
    console.log('Summarization effect triggered');
    const isDefaultTitle = ['New Chat', 'New Analysis', ''].includes(inquiryTitle.trim());
    console.log('isDefaultTitle:', inquiryTitle);
    console.log('hasSummarized:', hasSummarized);
    const shouldSummarize = !hasSummarized && messages.length > 3 && isDefaultTitle && currentInquiryId;
    if (!shouldSummarize) return;

    (async () => {
      try {
        const texts = messages.map((m) => m.body_original);
        const summRes = await fetch(`${BASE_ORCANA_URL}/summarizeMessages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ messages: texts }),
        });
        if (!summRes.ok) {
          console.error('Summarization failed:', await summRes.text());
          return;
        }
        const { summary } = await summRes.json();
        const updRes = await fetch(`${BASE_DB_URL}/update-inquiry/${currentInquiryId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ inquiry_title: summary }),
        });
        if (!updRes.ok) {
          console.error('Update title failed:', await updRes.text());
        } else {
          setHasSummarized(true);
          onInquiryUpdate?.();
        }
      } catch (e) {
        console.error('Summarization effect error:', e);
      }
    })();
  }, [messages, inquiryTitle, currentInquiryId, token, onInquiryUpdate, hasSummarized]);

  return {
    messages,
    messageDates,
    input,
    setInput,
    isSending,
    currentInquiryId,
    isConversationSelected,
    handleSendMessage,
    setMessages,
    setMessageDates,
    setCurrentInquiryId,
    setIsConversationSelected,
    clearChatState,
  };
};
